import React from "react";
import {
  Badge,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Close, Notifications } from "@mui/icons-material";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MarkEmailUnreadIcon from "@mui/icons-material/MarkEmailUnread";
import { useLocation } from "react-router-dom";
import { HtmlTooltip } from "../StyledComponents/styled";
import CloseButton from "../Buttons/CloseButton";
import LoadMoreButton from "../LoadMore/LoadMoreButton";
import { consumeAnnouncementAutoOpenPending, fetchToken } from "../../../api_calls/requestConfig";
import { checkUserDetailsFromCookie } from "../../../api_calls/userManagement";
import {
  announcementAudienceTypeForUser,
  canViewAnnouncementInbox,
} from "../../../api_calls/announcementAccess";
import {
  announcementSubscriptionEventType,
  fetchAnnouncements,
  fetchUnseenCount,
  markAllAnnouncementsSeen,
  markAnnouncementSeen,
  subscribeAnnouncementsChanged,
} from "../../../api_calls/announcementRequests";
import {
  ANNOUNCEMENT_FEED_PAGE_SIZE,
  formatAnnouncementDate,
  sortAnnouncementsByPublishDateDesc,
  UNREAD_DOT_COLOR,
} from "../../../api_calls/announcementUtils";
import AnnouncementHtml from "./AnnouncementHtml";

const PUBLIC_PATHS = ["/", "/login", "/forgot-password"];

// MUI modal default is 1300. Keep the bell on the page layer, the side panel
// above any open app dialog, and the detail popup above the panel.
const ANNOUNCEMENT_BELL_Z_INDEX = 1000;
const ANNOUNCEMENT_PANEL_Z_INDEX = 1450;
const ANNOUNCEMENT_DETAIL_Z_INDEX = 1550;

const applySeenOverlays = (serverItems, overlay, markAllIds) =>
  (serverItems || []).map((item) => {
    const id = String(item.id);
    if (overlay.has(id)) {
      return { ...item, seen: overlay.get(id) === true };
    }
    if (markAllIds && markAllIds.has(id)) {
      return { ...item, seen: true };
    }
    return { ...item, seen: item.seen === true };
  });

const countWithOverlays = (serverCount, serverItems, overlay, markAllIds) => {
  const items = serverItems || [];
  if (markAllIds) {
    let count = 0;
    items.forEach((item) => {
      const id = String(item.id);
      if (markAllIds.has(id)) {
        if (overlay.get(id) === false) {
          count += 1;
        }
        return;
      }
      const seen = overlay.has(id) ? overlay.get(id) === true : item.seen === true;
      if (seen !== true) {
        count += 1;
      }
    });
    return count;
  }

  let count = Math.max(0, Number(serverCount) || 0);
  const serverSeenById = new Map();
  items.forEach((item) => {
    serverSeenById.set(String(item.id), item.seen === true);
  });
  overlay.forEach((seen, id) => {
    const hasServerRow = serverSeenById.has(id);
    const serverSeen = hasServerRow ? serverSeenById.get(id) : null;
    if (seen === true) {
      if (serverSeen !== true) {
        count = Math.max(0, count - 1);
      }
    } else if (serverSeen === true || hasServerRow !== true) {
      count += 1;
    }
  });
  return count;
};

const subscriptionRecordId = (payload) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  if (payload.id != null) {
    return payload.id;
  }
  if (payload.announcementId != null) {
    return payload.announcementId;
  }
  if (payload.content && typeof payload.content === "object" && payload.content.id != null) {
    return payload.content.id;
  }
  return null;
};

const AnnouncementInbox = () => {
  const [panelOpen, setPanelOpen] = React.useState(false);
  const [publishedItems, setPublishedItems] = React.useState([]);
  const [feedSize, setFeedSize] = React.useState(ANNOUNCEMENT_FEED_PAGE_SIZE);
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [markingAll, setMarkingAll] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [detailItem, setDetailItem] = React.useState(null);
  const [hoveredId, setHoveredId] = React.useState(null);
  const previousUnreadCountRef = React.useRef(null);
  const unreadCountRef = React.useRef(0);
  const publishedItemsRef = React.useRef([]);
  const serverItemsRef = React.useRef([]);
  const serverUnseenCountRef = React.useRef(0);
  const overlayRef = React.useRef(new Map());
  const baselineRef = React.useRef(new Map());
  const markAllIdsRef = React.useRef(null);
  const seenAbortRef = React.useRef(new Map());
  const panelOpenRef = React.useRef(false);
  const feedSizeRef = React.useRef(ANNOUNCEMENT_FEED_PAGE_SIZE);
  const markingAllRef = React.useRef(false);
  const seenRequestSeqRef = React.useRef(new Map());
  const unpublishedAtRequestIdRef = React.useRef(new Map());
  const unseenRequestIdRef = React.useRef(0);
  const lastAppliedUnseenIdRef = React.useRef(0);

  const updateFeedSize = React.useCallback((nextSize) => {
    const size = Number(nextSize) > 0 ? Number(nextSize) : ANNOUNCEMENT_FEED_PAGE_SIZE;
    feedSizeRef.current = size;
    setFeedSize(size);
  }, []);

  const applyUnreadCount = React.useCallback((nextCount, options = {}) => {
    const next = Math.max(0, Number(nextCount) || 0);
    if (
      options.allowAutoOpen === true &&
      next > 0 &&
      (previousUnreadCountRef.current == null || next > previousUnreadCountRef.current)
    ) {
      const wasOpen = panelOpenRef.current === true;
      setPanelOpen(true);
      if (wasOpen !== true) {
        updateFeedSize(ANNOUNCEMENT_FEED_PAGE_SIZE);
      }
    }
    unreadCountRef.current = next;
    setUnreadCount(next);
    previousUnreadCountRef.current = next;
  }, [updateFeedSize]);

  const recomputeDisplay = React.useCallback((options = {}) => {
    const overlay = overlayRef.current;
    const markAllIds = markAllIdsRef.current;
    const serverItems = serverItemsRef.current;
    const confirmedIds = [];
    overlay.forEach((seen, id) => {
      const serverItem = (serverItems || []).find((item) => String(item.id) === id);
      if (serverItem && serverItem.seen === seen) {
        confirmedIds.push(id);
      }
    });
    confirmedIds.forEach((id) => {
      const serverItem = (serverItems || []).find((item) => String(item.id) === id);
      overlay.delete(id);
      if (serverItem) {
        baselineRef.current.set(id, serverItem.seen === true);
      }
    });
    const withSeen = applySeenOverlays(serverItems, overlay, markAllIds);
    publishedItemsRef.current = withSeen;
    setPublishedItems(withSeen);
    setDetailItem((current) => {
      if (!current) {
        return current;
      }
      const updated = withSeen.find((item) => String(item.id) === String(current.id));
      return updated || null;
    });
    applyUnreadCount(
      options.useServerCountDirect === true
        ? Math.max(0, Number(serverUnseenCountRef.current) || 0)
        : countWithOverlays(serverUnseenCountRef.current, serverItems, overlay, markAllIds),
      options
    );
  }, [applyUnreadCount]);

  const applyServerList = React.useCallback((announcements, requestId) => {
    const published = sortAnnouncementsByPublishDateDesc((announcements || []).filter((item) => item.published));
    const next = published.filter((item) => {
      const id = String(item.id);
      const removedAt = unpublishedAtRequestIdRef.current.get(id);
      if (removedAt == null) {
        return true;
      }
      if (requestId <= removedAt) {
        return false;
      }
      unpublishedAtRequestIdRef.current.delete(id);
      return true;
    });
    serverItemsRef.current = next;
    next.forEach((item) => {
      const id = String(item.id);
      if (!overlayRef.current.has(id)) {
        baselineRef.current.set(id, item.seen === true);
      }
    });
  }, []);

  const abortSeenRequest = React.useCallback((id) => {
    const controller = seenAbortRef.current.get(id);
    if (controller) {
      controller.abort();
      seenAbortRef.current.delete(id);
    }
  }, []);

  const abortAllSeenRequests = React.useCallback(() => {
    seenAbortRef.current.forEach((controller) => {
      controller.abort();
    });
    seenAbortRef.current.clear();
  }, []);

  const refreshRequestIdRef = React.useRef(0);
  const lastAppliedRequestIdRef = React.useRef(0);
  const mountedRef = React.useRef(true);

  const refreshInbox = React.useCallback(async (options = {}) => {
    const requestId = refreshRequestIdRef.current + 1;
    refreshRequestIdRef.current = requestId;
    const unseenId = unseenRequestIdRef.current + 1;
    unseenRequestIdRef.current = unseenId;
    const size =
      Number.isInteger(Number(options.size)) && Number(options.size) > 0
        ? Number(options.size)
        : feedSizeRef.current;
    const [result, unseenResult] = await Promise.all([
      fetchAnnouncements(true, {
        type: announcementAudienceTypeForUser(checkUserDetailsFromCookie(false).user_detail),
        page: 0,
        size,
        sort_by: "published_timestamp",
        sort_dir: "desc",
      }),
      fetchUnseenCount(),
    ]);
    if (!mountedRef.current || requestId < lastAppliedRequestIdRef.current) {
      return result;
    }
    let changed = false;
    if (result.success === true) {
      lastAppliedRequestIdRef.current = requestId;
      applyServerList(result.announcements, requestId);
      setHasMore(result.hasMore === true);
      changed = true;
    }
    if (unseenResult.success === true && unseenId >= lastAppliedUnseenIdRef.current) {
      lastAppliedUnseenIdRef.current = unseenId;
      serverUnseenCountRef.current = unseenResult.count;
      changed = true;
    }
    if (changed) {
      recomputeDisplay(options);
    }
    return result;
  }, [applyServerList, recomputeDisplay]);

  const refreshUnseenCountOnly = React.useCallback(async (options = {}) => {
    const unseenId = unseenRequestIdRef.current + 1;
    unseenRequestIdRef.current = unseenId;
    const result = await fetchUnseenCount();
    if (!mountedRef.current || unseenId < lastAppliedUnseenIdRef.current) {
      return result;
    }
    if (result.success === true) {
      lastAppliedUnseenIdRef.current = unseenId;
      serverUnseenCountRef.current = result.count;
      recomputeDisplay({ ...options, useServerCountDirect: true });
    }
    return result;
  }, [recomputeDisplay]);

  const refreshInboxRef = React.useRef(refreshInbox);
  const refreshUnseenCountOnlyRef = React.useRef(refreshUnseenCountOnly);
  React.useEffect(() => {
    refreshInboxRef.current = refreshInbox;
  }, [refreshInbox]);
  React.useEffect(() => {
    refreshUnseenCountOnlyRef.current = refreshUnseenCountOnly;
  }, [refreshUnseenCountOnly]);

  React.useEffect(() => {
    mountedRef.current = true;
    // auto-open with unread items only on the first mount after login, not on F5 / workspace switch
    refreshInboxRef.current({ allowAutoOpen: consumeAnnouncementAutoOpenPending() });
    return () => {
      mountedRef.current = false;
      refreshRequestIdRef.current += 1;
      abortAllSeenRequests();
    };
  }, [abortAllSeenRequests]);

  React.useEffect(() => {
    panelOpenRef.current = panelOpen;
    if (!panelOpen) {
      updateFeedSize(ANNOUNCEMENT_FEED_PAGE_SIZE);
      return undefined;
    }
    updateFeedSize(ANNOUNCEMENT_FEED_PAGE_SIZE);
    refreshInboxRef.current({ size: ANNOUNCEMENT_FEED_PAGE_SIZE });
    return undefined;
  }, [panelOpen, updateFeedSize]);

  React.useEffect(() => {
    const unsubscribeChanged = subscribeAnnouncementsChanged((event) => {
      const eventType = announcementSubscriptionEventType(event?.detail);
      if (eventType === "unpublished" || eventType === "unpublish") {
        const unpublishedId = subscriptionRecordId(event?.detail);
        if (unpublishedId != null) {
          const id = String(unpublishedId);
          abortSeenRequest(id);
          seenRequestSeqRef.current.set(id, (seenRequestSeqRef.current.get(id) || 0) + 1);
          overlayRef.current.delete(id);
          baselineRef.current.delete(id);
          if (markAllIdsRef.current) {
            markAllIdsRef.current.delete(id);
          }
          unpublishedAtRequestIdRef.current.set(id, refreshRequestIdRef.current);
          serverItemsRef.current = (serverItemsRef.current || []).filter((item) => String(item.id) !== id);
          recomputeDisplay();
        }
        refreshUnseenCountOnlyRef.current();
        return;
      }
      if (eventType === "published") {
        refreshInboxRef.current({ allowAutoOpen: true });
      }
    });
    return unsubscribeChanged;
  }, [abortSeenRequest, recomputeDisplay]);

  const markSeen = (announcement, seen) => {
    const id = String(announcement.id);
    const displayed = (publishedItemsRef.current || []).find((item) => String(item.id) === id);
    const currentSeen = displayed ? displayed.seen === true : announcement.seen === true;
    if (currentSeen === seen) {
      return;
    }

    overlayRef.current.set(id, seen === true);
    recomputeDisplay();

    const requestSeq = (seenRequestSeqRef.current.get(id) || 0) + 1;
    seenRequestSeqRef.current.set(id, requestSeq);
    abortSeenRequest(id);
    const controller = new AbortController();
    seenAbortRef.current.set(id, controller);

    markAnnouncementSeen(announcement.id, seen === true, controller.signal).then((result) => {
      if (!mountedRef.current || seenRequestSeqRef.current.get(id) !== requestSeq) {
        return;
      }
      seenAbortRef.current.delete(id);
      if (result.aborted === true) {
        return;
      }
      if (result.success === true) {
        baselineRef.current.set(id, seen === true);
        return;
      }
      const serverItem = (serverItemsRef.current || []).find((item) => String(item.id) === id);
      const serverSeen = serverItem ? serverItem.seen === true : false;
      const revertTo = baselineRef.current.has(id) ? baselineRef.current.get(id) === true : serverSeen;
      if (revertTo === serverSeen) {
        overlayRef.current.delete(id);
      } else {
        overlayRef.current.set(id, revertTo);
      }
      recomputeDisplay();
    });
  };

  const openDetail = (announcement) => {
    setDetailItem(announcement);
    if (announcement.seen !== true) {
      markSeen(announcement, true);
    }
  };

  const handleMarkAllRead = () => {
    if (markingAllRef.current) {
      return;
    }
    markingAllRef.current = true;
    setMarkingAll(true);
    seenRequestSeqRef.current.forEach((seq, id) => {
      seenRequestSeqRef.current.set(id, seq + 1);
    });
    abortAllSeenRequests();
    overlayRef.current = new Map();
    markAllIdsRef.current = new Set((serverItemsRef.current || []).map((item) => String(item.id)));
    recomputeDisplay();

    markAllAnnouncementsSeen().then((result) => {
      markingAllRef.current = false;
      if (mountedRef.current) {
        setMarkingAll(false);
      }
      if (!mountedRef.current) {
        return;
      }
      const snapshotIds = markAllIdsRef.current;
      markAllIdsRef.current = null;
      if (result.success === true) {
        let remaining = 0;
        (serverItemsRef.current || []).forEach((item) => {
          const id = String(item.id);
          if (snapshotIds && snapshotIds.has(id)) {
            return;
          }
          const seen = overlayRef.current.has(id)
            ? overlayRef.current.get(id) === true
            : item.seen === true;
          if (seen !== true) {
            remaining += 1;
          }
        });
        (snapshotIds || new Set()).forEach((id) => {
          if (overlayRef.current.get(id) === false) {
            return;
          }
          overlayRef.current.set(id, true);
          baselineRef.current.set(id, true);
        });
        serverUnseenCountRef.current = remaining;
        recomputeDisplay();
        return;
      }
      overlayRef.current = new Map();
      recomputeDisplay();
    });
  };

  const handleLoadMore = async () => {
    if (loadingMore === true || hasMore !== true) {
      return;
    }
    const nextSize = feedSize + ANNOUNCEMENT_FEED_PAGE_SIZE;
    updateFeedSize(nextSize);
    setLoadingMore(true);
    try {
      await refreshInbox({ size: nextSize });
    } finally {
      if (mountedRef.current) {
        setLoadingMore(false);
      }
    }
  };

  return (
    <>
      <Badge
        className="mui-fixed"
        badgeContent={unreadCount}
        invisible={unreadCount < 1}
        max={99}
        overlap="circular"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: ANNOUNCEMENT_BELL_Z_INDEX,
          "& .MuiBadge-badge": {
            minWidth: 22,
            height: 22,
            padding: "0 6px",
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#fff",
            color: "#AF3241",
            border: "2px solid #AF3241",
            boxSizing: "border-box",
            top: 6,
            right: 6,
          },
        }}
      >
        <IconButton
          id="announcementInboxBellButton"
          aria-label="Open announcements"
          onClick={() => {
            setPanelOpen(true);
          }}
          sx={{
            bgcolor: "#AF3241",
            color: "#fff",
            boxShadow: 3,
            width: 48,
            height: 48,
            "&:hover": {
              bgcolor: "#AF3241",
            },
          }}
        >
          <Notifications />
        </IconButton>
      </Badge>

      <Drawer
        anchor="right"
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        transitionDuration={detailItem ? 0 : undefined}
        PaperProps={{
          className: "mui-fixed",
        }}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: Boolean(detailItem),
        }}
        sx={{
          zIndex: ANNOUNCEMENT_PANEL_Z_INDEX,
          "& .MuiDrawer-paper": {
            width: { xs: "100%", sm: 420 },
            height: "100%",
            maxHeight: "100dvh",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            p: 2,
            pb: "max(16px, env(safe-area-inset-bottom))",
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexShrink: 0 }}>
          <Typography variant="h6">Announcements</Typography>
          <Stack direction="row" spacing={1}>
            <HtmlTooltip title="Mark all as read">
              <span>
                <IconButton
                  aria-label="Mark all as read"
                  onClick={handleMarkAllRead}
                  disabled={markingAll || (unreadCount < 1 && hasMore !== true)}
                >
                  <MarkEmailReadIcon />
                </IconButton>
              </span>
            </HtmlTooltip>
            <IconButton aria-label="Close announcements" onClick={() => setPanelOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack
            divider={<Divider sx={{ borderColor: "rgba(98, 98, 110, 0.14)" }} />}
            spacing={0}
          >
            {publishedItems.length === 0 && (
              <Typography variant="body2" sx={{ color: "#62626E", px: 1.5, py: 1 }}>
                No announcements
              </Typography>
            )}
            {publishedItems.map((announcement) => {
              const unread = announcement.seen !== true;
              return (
                <Box
                  key={announcement.id}
                  onMouseEnter={() => setHoveredId(announcement.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => openDetail(announcement)}
                  sx={{
                    position: "relative",
                    px: 1.5,
                    py: 1.5,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "rgba(240, 75, 85, 0.06)",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 10, pt: 0.8 }}>
                      {unread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: UNREAD_DOT_COLOR,
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1, pr: 4 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: unread ? 700 : 400, mb: 0.5, lineHeight: 1.4, whiteSpace: "normal", overflowWrap: "anywhere" }}
                      >
                        {announcement.title}
                      </Typography>
                      <AnnouncementHtml html={announcement.description} clampLines={4} />
                      <Typography variant="caption" sx={{ color: "#62626E", display: "block", mt: 1 }}>
                        {announcement.published_timestamp
                          ? formatAnnouncementDate(announcement.published_timestamp)
                          : ""}
                      </Typography>
                    </Box>
                  </Stack>
                  <HtmlTooltip title={unread ? "Mark as read" : "Mark as unread"}>
                    <IconButton
                      aria-label={unread ? "Mark as read" : "Mark as unread"}
                      size="small"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        markSeen(announcement, unread);
                      }}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        opacity: hoveredId === announcement.id ? 1 : 0,
                        pointerEvents: hoveredId === announcement.id ? "auto" : "none",
                      }}
                    >
                      {unread ? <MarkEmailReadIcon fontSize="small" /> : <MarkEmailUnreadIcon fontSize="small" />}
                    </IconButton>
                  </HtmlTooltip>
                </Box>
              );
            })}
          </Stack>
          {hasMore && (
            <Box sx={{ mt: 1, mb: 1, display: "flex", justifyContent: "center" }}>
              <LoadMoreButton clickAction={handleLoadMore} iconFontSize="2.4em" />
            </Box>
          )}
        </Box>

        <Box sx={{ pt: 2, flexShrink: 0 }}>
          <CloseButton
            onClick={(event) => {
              event.preventDefault();
              setPanelOpen(false);
            }}
            label="Close"
            icon={<Close />}
          />
        </Box>
      </Drawer>

      <Dialog
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        fullWidth
        maxWidth="md"
        disableScrollLock
        sx={{ zIndex: ANNOUNCEMENT_DETAIL_Z_INDEX }}
      >
        <DialogTitle>
          {detailItem?.title}
          {detailItem?.published_timestamp && (
            <Typography
              variant="caption"
              component="span"
              sx={{ color: "#62626E", fontWeight: 400, display: "block", mt: 0.75 }}
            >
              {formatAnnouncementDate(detailItem.published_timestamp)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {detailItem && <AnnouncementHtml html={detailItem.description} />}
        </DialogContent>
        <DialogActions>
          <CloseButton
            onClick={(event) => {
              event.preventDefault();
              setDetailItem(null);
            }}
            label="Close"
            icon={<Close />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export const AnnouncementInboxHost = () => {
  const location = useLocation();
  const [, forceRender] = React.useState(0);
  const token = fetchToken();
  // no logout here, the host is also mounted on the login page and during login (see checkUserDetailsFromCookie)
  const checkUserDetails = checkUserDetailsFromCookie(false);
  const userDetail = checkUserDetails.success === true ? checkUserDetails.user_detail : null;

  React.useEffect(() => {
    if (PUBLIC_PATHS.includes(location.pathname)) {
      return undefined;
    }
    if (token && userDetail) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (fetchToken() && checkUserDetailsFromCookie(false).success === true) {
        forceRender((value) => value + 1);
        window.clearInterval(intervalId);
      }
    }, 200);
    const timeoutId = window.setTimeout(() => window.clearInterval(intervalId), 10000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [location.pathname, token, userDetail]);

  if (!token || PUBLIC_PATHS.includes(location.pathname)) {
    return null;
  }
  if (!canViewAnnouncementInbox(userDetail)) {
    return null;
  }

  return <AnnouncementInbox key={userDetail?.user_name || token} />;
};

export default AnnouncementInbox;
